/**
 * The other kind of person in the building.
 *
 * Staff have opinions about you that turn into write-ups. Guests have opinions
 * about you that turn into complaints, and a complaint goes to Management —
 * which is worse, because it arrives second-hand and you are not in the room
 * for it. That is the whole of the difference, and it lives in one field:
 * `complains`. A guest is not a special case anywhere in the logic.
 *
 * They are in the lobby, because that is where guests are. Which is what makes
 * the marble expensive twice over: everything you do out there is watched by
 * the one class of person who does not work here and does not owe you anything.
 *
 * There is no romance layer, no affinity, and nothing to unlock. They are
 * people in a hotel.
 */

/** Guests look where they are walking and not much further. */
const GUEST_EYES = { range: 24, halfAngle: 60, dwell: 1.4 };

/**
 * Somebody in a pool has nothing to do but look. Further, wider and quicker
 * to be sure than anybody walking through a lobby: the deck is the view.
 */
const POOL_EYES = { range: 70, halfAngle: 80, dwell: 1 };

/*
 * In the Sunstone Pool, every afternoon: four guests in the water, drifting
 * between two spots each. `wading` is how deep the water they are stood in is,
 * in feet, which is how far below the deck they are drawn; their eyes are
 * where anybody's are, just over the edge.
 */
const POOL_SPOTS = [
  [[312, -66, 40], [326, -58, 40]],
  [[342, -70, 50], [354, -62, 30]],
  [[360, -50, 40], [348, -46, 40]],
  [[318, -48, 60], [332, -52, 30]],
];
const POOL_LINES = [
  'Are you setting up for something out here?',
  'Is it a party? Can we come?',
  'That table is wobbling. Just so you know.',
  'Honey, look, they are setting up a whole thing.',
];

/*
 * Two guests for a dinner at the Caytons, driven there in the golf cart
 * (`data/shuttles.js`). Nowhere at all until somebody is asked to drive them;
 * the shuttle puts them in the staff lot, by the cart.
 */
const SHUTTLE_LINES = ['Are we late? We are going to be late.', 'Is it far? Nobody said it was far.'];

/*
 * The dinner on the Breeze Lawn, eight covers at two tables, on a Friday.
 *
 * Guests like the rest: a seat for a route, a window, eyes, and `complains`.
 * What they have that the lobby does not is `dinner`, which is the event they
 * are sat at, and `faces`: seated guests at a lawn dinner all look the same
 * way, west, across the lawn toward the gate and the last of the light. That
 * is not dressing. It is which way they are looking when something comes
 * through the gate. See `data/dinners.js`.
 */
const DINNER_SEATS = [
  [176.4, 94], [176.4, 98], [183.6, 94], [183.6, 98],
  [186.4, 94], [186.4, 98], [193.6, 94], [193.6, 98],
];
const DINNER_LINES = [
  'This is lovely. Is it always this quiet out here?',
  'They said the stars come out about eight.',
  'Could we get another bottle of the white?',
  'I did not know the hotel had a lawn.',
  'My husband wanted the ballroom. I wanted this.',
  'Is it all right to take my shoes off?',
  'Something is moving over by the fence.',
  'Who organises one of these? I want their number.',
];

export const GUESTS = [
  {
    id: 'guest-checkin',
    name: 'Guest at the desk',
    department: 'guests',
    complains: true,
    // South of the seating rather than through it: the loop used to cut the
    // corner of both sofas.
    route: [
      [150, 34, 6],
      [176, 40, 5],
      [150, 43, 4],
      [132, 40, 3],
    ],
    speed: 1.1,
    window: null,
    vision: GUEST_EYES,
    price: {},
    line: 'Is there somewhere I can leave these?',
  },
  {
    id: 'guest-lounge',
    name: 'Guest in the lounge',
    department: 'guests',
    complains: true,
    // In front of the seats rather than through them. A guest in the lounge
    // paces along the sofas; the route used to go straight down the middle.
    route: [
      [142, 56, 12],
      [158, 56, 10],
    ],
    speed: 0.9,
    window: null,
    vision: GUEST_EYES,
    price: {},
    line: 'We were told the ballroom would be ready by six.',
  },
  {
    id: 'guest-corridor',
    name: 'Guest looking for the lifts',
    department: 'guests',
    complains: true,
    route: [
      [120, 20, 3],
      [120, 66, 3],
      [166, 66, 3],
      [120, 66, 0],
    ],
    speed: 1.3,
    window: null,
    vision: GUEST_EYES,
    price: {},
    line: 'Sorry, which way is the car park?',
  },
  {
    /** Waiting on the restroom, which is why it matters that it is theirs. */
    id: 'guest-restroom',
    name: 'Guest by the restrooms',
    department: 'guests',
    complains: true,
    // The turn is two feet short of the front desk's corner, not on it.
    route: [
      [186, 66, 14],
      [176, 65, 4],
    ],
    speed: 1,
    window: null,
    vision: GUEST_EYES,
    price: {},
    line: 'Is someone in there?',
  },
  {
    /*
     * Somebody on the way back from the courts.
     *
     * The first guest who is anywhere but the lobby, and the reason is the
     * Caytons: lit courts at the end of the cart road, and a guest who parked
     * in the staff lot because it is nearest the road. In the evening
     * somebody is always walking between the lot and the guest parking down
     * the West Drive, which is when the desert is out.
     *
     * The rest of the walk, the road itself, is a minute in a golf cart and
     * twenty on foot, and guests do not walk it; this row is the half of the
     * walk that is at the hotel's end. The rest is where guests always are
     * when they are not on the floor — not anywhere.
     */
    id: 'guest-caytons',
    name: 'Guest back from the courts',
    department: 'guests',
    complains: true,
    // The one who is out past the courts when a lion is (`data/rescues.js`).
    strands: 'lion-rescue',
    route: [
      [-20, -100, 4],
      [-58, -94, 0],
      [-76, -94, 0],
      [-76, 60, 10],
      [-76, -94, 0],
      [-58, -94, 0],
    ],
    speed: 1.1,
    window: { from: '17:00', to: '22:00' },
    vision: GUEST_EYES,
    price: {},
    line: 'Are those pigs? Is that a pig? Are those wild pigs?',
  },
  // Last, so the building's own guests stay where they were in the list.
  ...DINNER_SEATS.map(([x, z], index) => ({
    id: `guest-dinner-${index + 1}`,
    name: 'Guest at the lawn dinner',
    department: 'guests',
    complains: true,
    dinner: 'breeze-dinner',
    route: [[x, z, 600]],
    faces: -Math.PI / 2,
    speed: 1.1,
    window: { from: '19:00', to: '22:00' },
    // Friday, which is day five: Monday is the first day of the week.
    days: [4],
    vision: GUEST_EYES,
    price: {},
    line: DINNER_LINES[index],
  })),
  // After the dinner guests, so they and everybody before them stay where they were.
  ...POOL_SPOTS.map((route, index) => ({
    id: `guest-pool-${index + 1}`,
    name: 'Guest in the pool',
    department: 'guests',
    complains: true,
    wading: 3.5,
    route,
    speed: 0.5,
    window: { from: '10:00', to: '19:30' },
    vision: POOL_EYES,
    price: {},
    line: POOL_LINES[index],
  })),
  ...SHUTTLE_LINES.map((line, index) => ({
    id: `guest-shuttle-${index + 1}`,
    name: 'Guest for the Caytons',
    department: 'guests',
    complains: true,
    shuttle: 'caytons-shuttle',
    route: [[index ? -33 : -47, -78, 600]],
    speed: 1.1,
    window: { from: '17:00', to: '23:00' },
    vision: GUEST_EYES,
    price: {},
    line,
  })),
  /*
   * The building in its own time (D's brief, a living building). Last in the
   * list, like the dinner, so everybody before them is where they were.
   *
   * A couple checking in: one at the desk, one minding the bags, then both up
   * to the lifts on the east wall and back down for the rest of the luggage.
   */
  {
    id: 'guest-arrival-a',
    name: 'Guest checking in',
    department: 'guests',
    complains: true,
    route: [
      [150, 74, 5],
      [168, 64, 0],
      [168, 40, 0],
      [176.6, 40, 40],
      [176.6, 26, 0],
      [192, 19, 22],
      [168, 26, 0],
      [168, 64, 0],
    ],
    speed: 1.0,
    window: { from: '09:00', to: '21:00' },
    vision: GUEST_EYES,
    price: {},
    line: 'Is it always this warm in here?',
  },
  {
    id: 'guest-arrival-b',
    name: 'Guest with the bags',
    department: 'guests',
    complains: true,
    route: [
      [148, 74, 5],
      [166, 64, 0],
      [166, 42, 40],
      [166, 26, 0],
      [190, 22, 22],
      [166, 26, 0],
      [166, 64, 0],
    ],
    speed: 1.0,
    window: { from: '09:00', to: '21:00' },
    vision: GUEST_EYES,
    price: {},
    line: 'These are not all mine. Most of them are mine.',
  },
  // Sat in the lounge with a coffee and the paper, for the afternoon.
  {
    id: 'guest-sofa-a',
    name: 'Guest on the sofa',
    department: 'guests',
    complains: true,
    // Sat facing into the lounge, which is the way the settee faces.
    route: [[142, 44.5, 900]],
    faces: 0,
    speed: 0.8,
    window: { from: '10:00', to: '22:00' },
    vision: GUEST_EYES,
    price: {},
    line: 'Do you know if the bar does food?',
  },
  {
    id: 'guest-sofa-b',
    name: 'Guest reading',
    department: 'guests',
    complains: true,
    route: [[158, 44.5, 700]],
    faces: 0,
    speed: 0.8,
    window: { from: '12:00', to: '21:30' },
    vision: GUEST_EYES,
    price: {},
    line: 'Sorry, is this seat taken?',
  },
  // On the loungers by the pool, in the afternoon sun.
  {
    id: 'guest-lounger-a',
    name: 'Guest by the pool',
    department: 'guests',
    complains: true,
    // On the lounger, facing the water.
    route: [[278.3, -74.5, 900]],
    faces: Math.PI / 2,
    speed: 0.8,
    window: { from: '11:00', to: '18:00' },
    vision: GUEST_EYES,
    price: {},
    line: 'Could we get some more towels out here?',
  },
  {
    id: 'guest-lounger-b',
    name: 'Guest by the pool',
    department: 'guests',
    complains: true,
    route: [[278.3, -54.5, 800]],
    faces: Math.PI / 2,
    speed: 0.8,
    window: { from: '12:00', to: '18:30' },
    vision: GUEST_EYES,
    price: {},
    line: 'Is the bar by the pool open yet?',
  },
  // A knock-up on the far court at the Caytons (court D, south-east), back and
  // forth along the baselines: a long way from the strip behind the north
  // fence that `world.mjs` uses for nowhere.
  {
    id: 'guest-court-a',
    name: 'Guest on the courts',
    department: 'guests',
    complains: true,
    route: [
      [-293, 34, 2],
      [-283, 34, 2],
    ],
    speed: 1.7,
    window: { from: '08:00', to: '18:00' },
    vision: GUEST_EYES,
    price: {},
    line: 'Out. That was out.',
  },
  {
    id: 'guest-court-b',
    name: 'Guest on the courts',
    department: 'guests',
    complains: true,
    route: [
      [-283, 60, 2],
      [-293, 60, 2],
    ],
    speed: 1.7,
    window: { from: '08:00', to: '18:00' },
    vision: GUEST_EYES,
    price: {},
    line: 'That was in. That was well in.',
  },
  /*
   * Here for whatever is on in the ballroom tonight: in the lobby outside its
   * doors from a little before they open, and not otherwise (`attends`, asked
   * by `main.js` off the day's board). Busier around events, quieter without.
   */
  {
    id: 'guest-event-1',
    name: 'Guest for the event',
    department: 'guests',
    complains: true,
    attends: true,
    route: [
      [110, 28, 20],
      [113, 30, 15],
    ],
    speed: 0.9,
    window: null,
    vision: GUEST_EYES,
    price: {},
    line: 'Are they letting us in yet?',
  },
  {
    id: 'guest-event-2',
    name: 'Guest for the event',
    department: 'guests',
    complains: true,
    attends: true,
    route: [
      [112, 40, 25],
      [115, 37, 10],
    ],
    speed: 0.9,
    window: null,
    vision: GUEST_EYES,
    price: {},
    line: 'I told you we were early.',
  },
  {
    id: 'guest-event-3',
    name: 'Guest for the event',
    department: 'guests',
    complains: true,
    attends: true,
    route: [
      [116, 26, 18],
      [114, 24, 12],
    ],
    speed: 0.9,
    window: null,
    vision: GUEST_EYES,
    price: {},
    line: 'Is there a cloakroom?',
  },
  {
    id: 'guest-event-4',
    name: 'Guest for the event',
    department: 'guests',
    complains: true,
    attends: true,
    route: [
      [109, 44, 22],
      [112, 46, 14],
    ],
    speed: 0.9,
    window: null,
    vision: GUEST_EYES,
    price: {},
    line: 'Which table are we on?',
  },
  {
    id: 'guest-event-5',
    name: 'Guest for the event',
    department: 'guests',
    complains: true,
    attends: true,
    route: [
      [118, 42, 16],
      [119, 38, 20],
    ],
    speed: 0.9,
    window: null,
    vision: GUEST_EYES,
    price: {},
    line: 'I hope there is a bar inside.',
  },
  {
    id: 'guest-event-6',
    name: 'Guest for the event',
    department: 'guests',
    complains: true,
    attends: true,
    route: [
      [114, 34, 30],
      [116, 33, 10],
    ],
    speed: 0.9,
    window: null,
    vision: GUEST_EYES,
    price: {},
    line: 'Have you seen the bride?',
  },
];
